import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Download, ArrowRight, Rocket, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentTier, setPaymentTier] = useState('basic');
  const [accessCode, setAccessCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const verifiedRef = useRef(false);

  const copyAccessCode = async () => {
    try {
      await navigator.clipboard.writeText(accessCode);
      setCodeCopied(true);
      toast.success('Access code copied to clipboard');
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');
      const tier = searchParams.get('tier') || 'basic';
      setPaymentTier(tier);

      if (!paymentId || !payerId) {
        toast.error('Invalid payment parameters');
        setIsProcessing(false);
        return;
      }

      // One-shot guard: never call execute-payment twice for the same load
      // (a repeat call would hit PayPal's "already executed" and look like a failure).
      if (verifiedRef.current) return;
      verifiedRef.current = true;

      try {
        const response = await axios.post(`${API}/execute-payment?tier=${tier}`, {
          payment_id: paymentId,
          payer_id: payerId
        });

        if (response.data.status === 'success') {
          setPaymentVerified(true);

          // Cache the email (non-sensitive) for the Pro Dashboard login form prefill.
          // The actual auth token is delivered as an httpOnly cookie set by the server
          // and is not accessible from JS — that's a security feature, not a bug.
          if (response.data.email) {
            localStorage.setItem('pro_email', response.data.email);
          }

          // Store access code for Pro users (shown on screen so they can save it)
          if (response.data.access_code) {
            setAccessCode(response.data.access_code);
          }

          if (response.data.demo_mode) {
            toast.success('Demo payment completed!');
          } else {
            toast.success('Payment verified successfully!');
          }
        }
      } catch (error) {
        const detail = error.response?.data?.detail || 'Payment verification failed';
        setErrorDetail(typeof detail === 'string' ? detail : 'Payment verification failed');
        toast.error(detail);
      } finally {
        setIsProcessing(false);
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const downloadInstructions = () => {
    const instructions = `
MILLIONAIRE THE MACHINE - SETUP INSTRUCTIONS
==============================================

Congratulations! You now have access to the complete Millionaire Machine system.

NEXT STEPS:

1. CLONE THE REPOSITORY
   - GitHub: https://github.com/yourusername/millionaire-machine
   - Run: git clone https://github.com/yourusername/millionaire-machine.git

2. SETUP PAYPAL BUSINESS ACCOUNT
   - Visit: https://www.paypal.com/business
   - Get your API credentials from the Developer Dashboard
   - Note: Use Sandbox mode for testing first

3. CONFIGURE YOUR INSTANCE
   - Copy .env.example to .env
   - Add your PayPal Client ID and Secret
   - Configure your MongoDB connection

4. INSTALL DEPENDENCIES
   Backend:
     cd backend
     pip install -r requirements.txt
   
   Frontend:
     cd frontend
     yarn install

5. RUN LOCALLY
   Backend:
     uvicorn server:app --reload --port 8001
   
   Frontend:
     yarn start

6. DEPLOY TO PRODUCTION
   - Deploy to Vercel, Netlify, or GitHub Pages
   - Set environment variables in your hosting platform
   - Switch PayPal to Live mode

7. SHARE & EARN
   - Share your instance URL on social media
   - Every $1 payment goes directly to YOUR PayPal account
   - Scale it up!

IMPORTANT REMINDERS:
- Never commit .env files to Git
- Keep your API keys secure
- Start with PayPal Sandbox mode for testing
- Follow all PayPal terms of service
- This is 100% legal - you're selling information and software licenses

SUPPORT:
For questions or issues, refer to the documentation in the repository.

Good luck building your Millionaire Machine!
`;

    const blob = new Blob([instructions], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'millionaire-machine-instructions.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Instructions downloaded!');
  };

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {isProcessing ? (
            <Card className="card-tech p-12 text-center" data-testid="processing-card">
              <div className="animate-spin w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-3xl font-bold text-green-400 mb-4">Processing Your Payment...</h2>
              <p className="text-green-300">Please wait while we verify your transaction.</p>
            </Card>
          ) : paymentVerified ? (
            <div className="space-y-8" data-testid="success-content">
              <Card className="card-tech p-12 text-center pulse-glow">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" data-testid="success-icon" />
                <h1 className="text-5xl font-bold mb-4 glow-text" data-testid="success-title">PAYMENT SUCCESSFUL!</h1>
                <p className="text-2xl text-green-300 mb-4" data-testid="success-message">
                  Welcome to {paymentTier === 'pro' ? 'PRO Edition' : 'Basic Edition'}
                </p>
                <p className="text-lg text-green-400 mb-2" data-testid="success-description">
                  You now have complete access to {paymentTier === 'pro' ? 'all features including AI tools' : 'the source code and documentation'}.
                </p>

                {/* Pro Access Code */}
                {paymentTier === 'pro' && accessCode && (
                  <div className="mt-6 p-4 bg-green-500/20 border-2 border-green-500/50 rounded-lg" data-testid="pro-access-info">
                    <p className="text-green-400 font-bold text-lg mb-2">Your Pro Access Code:</p>
                    <div className="flex items-stretch gap-2">
                      <code className="flex-1 text-green-300 font-mono bg-black/50 px-4 py-2 rounded break-all flex items-center" data-testid="access-code-value">{accessCode}</code>
                      <Button
                        onClick={copyAccessCode}
                        className="btn-secondary px-4 shrink-0"
                        data-testid="copy-access-code-btn"
                        aria-label="Copy access code"
                      >
                        {codeCopied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                      </Button>
                    </div>
                    <p className="text-green-400 text-sm mt-3">Save this! Use it with your PayPal email to log into the Pro Dashboard.</p>
                    <Button
                      onClick={() => navigate('/pro-dashboard')}
                      className="btn-primary mt-4 px-6 py-3"
                      data-testid="go-to-pro-dashboard"
                    >
                      Go to Pro Dashboard
                    </Button>
                  </div>
                )}

                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-300 text-base">
                    Check your PayPal email for complete access instructions.
                  </p>
                </div>
              </Card>

              <Card className="card-tech p-8" data-testid="access-card">
                <h2 className="text-3xl font-bold mb-6 text-green-400">Your Access Details</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start" data-testid="access-item-1">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-green-300 mb-1">Complete Source Code</h3>
                      <p className="text-green-400">Full access to the GitHub repository with all files</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start" data-testid="access-item-2">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-green-300 mb-1">Setup Documentation</h3>
                      <p className="text-green-400">Step-by-step guides for deployment and configuration</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start" data-testid="access-item-3">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-green-300 mb-1">PayPal Integration Guide</h3>
                      <p className="text-green-400">Complete instructions for setting up your payment system</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start" data-testid="access-item-4">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-green-300 mb-1">Security Best Practices</h3>
                      <p className="text-green-400">Encryption guidelines and credential storage utilities</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={downloadInstructions}
                    className="btn-primary px-8 py-6 text-lg"
                    data-testid="download-instructions-button"
                  >
                    <Download className="mr-2 w-5 h-5" />
                    Download Instructions
                  </Button>
                  <Button
                    onClick={() => navigate('/setup')}
                    className="btn-secondary px-8 py-6 text-lg"
                    data-testid="view-setup-button"
                  >
                    <Rocket className="mr-2 w-5 h-5" />
                    View Setup Guide
                  </Button>
                </div>
              </Card>

              <Card className="card-tech p-8" data-testid="next-steps-card">
                <h2 className="text-3xl font-bold mb-6 text-green-400">Next Steps</h2>
                <ol className="space-y-4 text-green-300 text-lg">
                  <li className="flex items-start" data-testid="next-step-1">
                    <span className="text-green-500 font-bold mr-3">1.</span>
                    <span>Download the setup instructions and keep them safe</span>
                  </li>
                  <li className="flex items-start" data-testid="next-step-2">
                    <span className="text-green-500 font-bold mr-3">2.</span>
                    <span>Create your PayPal Business account and get API credentials</span>
                  </li>
                  <li className="flex items-start" data-testid="next-step-3">
                    <span className="text-green-500 font-bold mr-3">3.</span>
                    <span>Clone the repository and configure your .env file</span>
                  </li>
                  <li className="flex items-start" data-testid="next-step-4">
                    <span className="text-green-500 font-bold mr-3">4.</span>
                    <span>Test locally, then deploy to your hosting platform</span>
                  </li>
                  <li className="flex items-start" data-testid="next-step-5">
                    <span className="text-green-500 font-bold mr-3">5.</span>
                    <span>Start sharing and earning!</span>
                  </li>
                </ol>
              </Card>

              <div className="text-center">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="btn-secondary px-8 py-4"
                  data-testid="back-home-button"
                >
                  <ArrowRight className="mr-2 w-5 h-5" />
                  Back to Home
                </Button>
              </div>
            </div>
          ) : (
            <Card className="card-tech p-12 text-center" data-testid="error-card">
              <h2 className="text-3xl font-bold text-red-400 mb-4">Payment Verification Failed</h2>
              <p className="text-green-300 mb-4">We could not confirm your payment on this page.</p>
              {errorDetail && (
                <div className="mb-6 mx-auto max-w-lg p-3 bg-red-500/10 border border-red-500/40 rounded text-left" data-testid="error-detail">
                  <p className="text-red-300 text-sm break-words"><span className="font-bold">Reason:</span> {errorDetail}</p>
                </div>
              )}
              <p className="text-green-400/80 text-sm mb-8">
                If you were charged, your access is safe — click Retry to confirm it. If it keeps failing,
                contact support with your payment ID (in the address bar).
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => window.location.reload()}
                  className="btn-primary px-8 py-4"
                  data-testid="retry-verify-button"
                >
                  Retry Verification
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="btn-secondary px-8 py-4"
                  data-testid="try-again-button"
                >
                  Back to Home
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;